import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  canonical, 
  ogImage, 
  ogType = 'website',
  structuredData,
  noIndex = false,
  author = 'OctalChip',
  publishDate,
  modifiedDate,
  articleSection,
  tags = [],
  locale = 'en_US',
  siteName = 'OctalChip',
  twitterHandle = '@octalchip',
  additionalMeta = [],
  breadcrumbs = [],
  faqData = [],
  reviewData = null,
  // GEO-specific props
  geoData = null,
  localBusinessData = null,
  serviceArea = null,
  // AEO-specific props
  howToData = [],
  articleData = null,
  productData = null,
  eventData = null,
  videoData = null,
  // Voice search optimization
  voiceSearchKeywords = [],
  conversationalQueries = [],
  // Featured snippet optimization
  featuredSnippetContent = null,
  tableData = null,
  listData = null
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    }
    
    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonical);
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', canonical);
    }
    
    const ogTypeMeta = document.querySelector('meta[property="og:type"]');
    if (ogTypeMeta) {
      ogTypeMeta.setAttribute('content', ogType);
    }
    
    if (ogImage) {
      const ogImageMeta = document.querySelector('meta[property="og:image"]');
      if (ogImageMeta) {
        ogImageMeta.setAttribute('content', ogImage);
      }
    }
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }
    
    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', canonical);
    }
    
    if (ogImage) {
      const twitterImage = document.querySelector('meta[property="twitter:image"]');
      if (twitterImage) {
        twitterImage.setAttribute('content', ogImage);
      }
    }
    
    // Update robots meta tag
    const robotsMeta = document.querySelector('meta[name="robots"]');
    if (robotsMeta) {
      robotsMeta.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    }
    
  }, [title, description, keywords, canonical, ogImage, ogType, noIndex]);

  // Generate comprehensive structured data
  const generateStructuredData = () => {
    const baseStructuredData = structuredData || {};
    
    // Add FAQ structured data if provided (AEO)
    // FAQ schema should be separate from other structured data
    if (faqData && faqData.length > 0) {
      // Return base structured data - FAQ will be added separately
      return baseStructuredData;
    }
    
    return baseStructuredData;
  };

  // Generate GEO-specific structured data
  const generateGeoStructuredData = () => {
    if (!geoData && !localBusinessData) return null;
    
    const geoStructuredData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "OctalChip",
      "description": "Leading web and mobile app development company offering AI-powered solutions, custom software development, cloud services, and digital transformation.",
      "url": "https://www.octalchip.com",
      "logo": "https://www.octalchip.com/favicon.svg",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "404, Opera point, kothariya",
        "addressLocality": "Gujarat",
        "addressRegion": "Gujarat",
        "addressCountry": "IN",
        "postalCode": "360022"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "23.0225",
        "longitude": "72.5714"
      },
      "openingHours": "Mo-Fr 09:00-18:00",
      "priceRange": "$$",
      "currenciesAccepted": "USD, INR",
      "paymentAccepted": "Cash, Credit Card, Bank Transfer",
      "areaServed": serviceArea || [
        {
          "@type": "Country",
          "name": "United States"
        },
        {
          "@type": "Country",
          "name": "United Kingdom"
        },
        {
          "@type": "Country", 
          "name": "Germany"
        },
        {
          "@type": "Country",
          "name": "France"
        },
        {
          "@type": "Country",
          "name": "Netherlands"
        },
        {
          "@type": "Country",
          "name": "Canada"
        },
        {
          "@type": "Country",
          "name": "Australia"
        },
        {
          "@type": "Country",
          "name": "UAE"
        },
        {
          "@type": "Country",
          "name": "Singapore"
        },
        {
          "@type": "Country",
          "name": "India"
        },
        {
          "@type": "Country",
          "name": "Saudi Arabia"
        },
        {
          "@type": "Country",
          "name": "Mexico"
        },
        {
          "@type": "Country",
          "name": "Brazil"
        },
        {
          "@type": "Country",
          "name": "Japan"
        },
        {
          "@type": "Country",
          "name": "South Korea"
        },
        {
          "@type": "Country",
          "name": "Poland"
        },
        {
          "@type": "Country",
          "name": "Romania"
        },
        {
          "@type": "Country",
          "name": "Bulgaria"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Development Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Web Development",
              "description": "Custom web applications and websites"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Mobile App Development",
              "description": "iOS and Android mobile applications"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "AI Development",
              "description": "AI-powered solutions and machine learning"
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "150",
        "bestRating": "5",
        "worstRating": "1"
      },
      "sameAs": [
        "https://www.facebook.com/people/Octalchip/61586959712930/",
        "https://www.instagram.com/octalchiptech/",
        "https://www.linkedin.com/company/octalchip",
        "https://x.com/octalchip",
        "https://discord.gg/p8Zcgatn9X",
        "https://www.youtube.com/@octalchip",
        "https://www.pinterest.com/octalchip/",
        "https://bsky.app/profile/octalchip.bsky.social",
        "https://www.reddit.com/user/octalchip",
        "https://t.me/octalchip",
        "https://www.threads.com/@octalchiptech",
        "https://medium.com/@octalchip",
        "https://github.com/octalchip"
      ]
    };

    return geoStructuredData;
  };

  // Generate AEO-specific structured data
  const generateAEOStructuredData = () => {
    const aeoData = [];

    // How-to structured data
    if (howToData && howToData.length > 0) {
      howToData.forEach(howTo => {
        aeoData.push({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": howTo.name,
          "description": howTo.description,
          "image": howTo.image,
          "totalTime": howTo.totalTime,
          "supply": howTo.supply ? howTo.supply.map(item => ({
            "@type": "HowToSupply",
            "name": item
          })) : undefined,
          "tool": howTo.tool ? howTo.tool.map(item => ({
            "@type": "HowToTool",
            "name": item
          })) : undefined,
          "step": howTo.steps ? howTo.steps.map((step, index) => ({
            "@type": "HowToStep",
            "position": index + 1,
            "name": step.name,
            "text": step.text,
            "image": step.image,
            "url": step.url
          })) : undefined
        });
      });
    }

    // Article structured data
    if (articleData) {
      aeoData.push({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": articleData.headline,
        "description": articleData.description,
        "image": articleData.image,
        "author": {
          "@type": "Organization",
          "name": "OctalChip"
        },
        "publisher": {
          "@type": "Organization",
          "name": "OctalChip",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.octalchip.com/favicon.svg"
          }
        },
        "datePublished": articleData.datePublished,
        "dateModified": articleData.dateModified,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonical
        }
      });
    }

    // Product structured data
    if (productData) {
      aeoData.push({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": productData.name,
        "description": productData.description,
        "image": productData.image,
        "brand": {
          "@type": "Brand",
          "name": "OctalChip"
        },
        "offers": {
          "@type": "Offer",
          "price": productData.price,
          "priceCurrency": productData.priceCurrency,
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "OctalChip"
          }
        }
      });
    }

    // Event structured data
    if (eventData) {
      aeoData.push({
        "@context": "https://schema.org",
        "@type": "Event",
        "name": eventData.name,
        "description": eventData.description,
        "startDate": eventData.startDate,
        "endDate": eventData.endDate,
        "location": {
          "@type": "Place",
          "name": eventData.location?.name,
          "address": eventData.location?.address
        },
        "organizer": {
          "@type": "Organization",
          "name": "OctalChip"
        }
      });
    }

    // Video structured data
    if (videoData) {
      aeoData.push({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": videoData.name,
        "description": videoData.description,
        "thumbnailUrl": videoData.thumbnailUrl,
        "uploadDate": videoData.uploadDate,
        "duration": videoData.duration,
        "contentUrl": videoData.contentUrl,
        "embedUrl": videoData.embedUrl
      });
    }

    return aeoData.length > 0 ? aeoData : null;
  };

  // Generate breadcrumb structured data
  const generateBreadcrumbStructuredData = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };
  };

  // Use useEffect to inject structured data directly into the DOM
  useEffect(() => {
    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add main structured data
    const mainData = generateStructuredData();
    if (mainData && Object.keys(mainData).length > 0) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(mainData);
      document.head.appendChild(script);
    }

    // Add GEO structured data
    const geoData = generateGeoStructuredData();
    if (geoData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(geoData);
      document.head.appendChild(script);
    }

    // Add AEO structured data
    const aeoData = generateAEOStructuredData();
    if (aeoData) {
      aeoData.forEach(data => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Add FAQ structured data (separate from other structured data)
    if (faqData && faqData.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(script);
    }

    // Add breadcrumb structured data
    const breadcrumbData = generateBreadcrumbStructuredData();
    if (breadcrumbData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(breadcrumbData);
      document.head.appendChild(script);
    }

    // Add review structured data
    if (reviewData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(reviewData);
      document.head.appendChild(script);
    }

    // Add table structured data
    if (tableData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Table",
        "name": tableData.name,
        "description": tableData.description,
        "about": tableData.about,
        "table": tableData.table
      });
      document.head.appendChild(script);
    }

    // Add list structured data
    if (listData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": listData.name,
        "description": listData.description,
        "numberOfItems": listData.items ? listData.items.length : 0,
        "itemListElement": listData.items ? listData.items.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "description": item.description,
          "url": item.url
        })) : []
      });
      document.head.appendChild(script);
    }

    // Add GEO meta tags
    if (geoData) {
      const geoRegion = document.querySelector('meta[name="geo.region"]');
      if (geoRegion) {
        geoRegion.setAttribute('content', geoData.region || "IN-GJ");
      } else {
        const meta = document.createElement('meta');
        meta.name = 'geo.region';
        meta.content = geoData.region || "IN-GJ";
        document.head.appendChild(meta);
      }

      const geoPlacename = document.querySelector('meta[name="geo.placename"]');
      if (geoPlacename) {
        geoPlacename.setAttribute('content', geoData.placename || "Gujarat, India");
      } else {
        const meta = document.createElement('meta');
        meta.name = 'geo.placename';
        meta.content = geoData.placename || "Gujarat, India";
        document.head.appendChild(meta);
      }

      const geoPosition = document.querySelector('meta[name="geo.position"]');
      if (geoPosition) {
        geoPosition.setAttribute('content', `${geoData.latitude || "23.0225"};${geoData.longitude || "72.5714"}`);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'geo.position';
        meta.content = `${geoData.latitude || "23.0225"};${geoData.longitude || "72.5714"}`;
        document.head.appendChild(meta);
      }

      const icbm = document.querySelector('meta[name="ICBM"]');
      if (icbm) {
        icbm.setAttribute('content', `${geoData.latitude || "23.0225"}, ${geoData.longitude || "72.5714"}`);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'ICBM';
        meta.content = `${geoData.latitude || "23.0225"}, ${geoData.longitude || "72.5714"}`;
        document.head.appendChild(meta);
      }
    }

    // Add voice search keywords
    if (voiceSearchKeywords && voiceSearchKeywords.length > 0) {
      const meta = document.querySelector('meta[name="voice-search-keywords"]');
      if (meta) {
        meta.setAttribute('content', voiceSearchKeywords.join(', '));
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'voice-search-keywords';
        newMeta.content = voiceSearchKeywords.join(', ');
        document.head.appendChild(newMeta);
      }
    }

    // Add conversational queries
    if (conversationalQueries && conversationalQueries.length > 0) {
      const meta = document.querySelector('meta[name="conversational-queries"]');
      if (meta) {
        meta.setAttribute('content', conversationalQueries.join(', '));
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'conversational-queries';
        newMeta.content = conversationalQueries.join(', ');
        document.head.appendChild(newMeta);
      }
    }

    // Add featured snippet content
    if (featuredSnippetContent) {
      const meta = document.querySelector('meta[name="featured-snippet-content"]');
      if (meta) {
        meta.setAttribute('content', featuredSnippetContent);
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'featured-snippet-content';
        newMeta.content = featuredSnippetContent;
        document.head.appendChild(newMeta);
      }
    }

  }, [title, description, keywords, canonical, ogImage, ogType, noIndex, structuredData, faqData, geoData, localBusinessData, serviceArea, howToData, articleData, productData, eventData, videoData, breadcrumbs, reviewData, voiceSearchKeywords, conversationalQueries, featuredSnippetContent, tableData, listData]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      
      {/* Language and Locale */}
      <html lang="en" />
      <meta property="og:locale" content={locale} />
      
      {/* Author and Publishing Info */}
      {author && <meta name="author" content={author} />}
      {publishDate && <meta name="article:published_time" content={publishDate} />}
      {modifiedDate && <meta name="article:modified_time" content={modifiedDate} />}
      {articleSection && <meta name="article:section" content={articleSection} />}
      
      {/* Tags */}
      {tags.map((tag, index) => (
        <meta key={index} name="article:tag" content={tag} />
      ))}
      
      {/* Open Graph Enhanced */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:secure_url" content={ogImage} />}
      {ogImage && <meta property="og:image:type" content="image/png" />}
      {ogImage && <meta property="og:image:width" content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      {ogImage && <meta property="og:image:alt" content={title} />}
      
      {/* Twitter Enhanced */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && <meta name="twitter:image:alt" content={title} />}
      
      {/* Robots */}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'} />
      
      {/* Additional Meta Tags */}
      {additionalMeta.map((meta, index) => (
        <meta key={index} name={meta.name} content={meta.content} />
      ))}
    </Helmet>
  );
};

export default SEO;